import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import {
  Activity, List, BarChart2, CheckCircle2, XCircle, Clock,
  TrendingUp, AlertTriangle, RefreshCw, Copy, Layers
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from "recharts";

const API = `${window.location.origin}/api`;

const PERIODS = [
  { key: "today", label: "Bugün" },
  { key: "week",  label: "Son 7 Gün" },
  { key: "month", label: "Son 30 Gün" },
  { key: "all",   label: "Tüm Zamanlar" },
];

function formatDt(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "-"; }
}

function pct(num, total) {
  if (!total) return "0%";
  return `%${Math.round((num / total) * 100)}`;
}

function StatCard({ label, value, sub, icon: Icon, color = "text-gray-900", bg = "bg-white", accent }) {
  return (
    <div className={`${bg} rounded-xl border border-gray-200 shadow-sm p-5 animate-fade-in-up transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{label}</p>
          <p className={`text-4xl font-bold leading-none ${color}`} style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {value}
          </p>
          {sub && <p className="text-xs text-gray-400 mt-2 truncate">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${accent || "bg-gray-100"}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: "inherit" }} />
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-bold text-gray-700 mb-1.5">{label}</p>
      {payload.map(p => (
        <p key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.fill }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-bold text-gray-800">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { token } = useAuth();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("today");
  const [refreshing, setRefreshing] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard`, { headers, params: { period } });
      setData(res.data);
    } catch {
      toast.error("Dashboard verisi yüklenemedi");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]); // eslint-disable-line

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Pie chart data
  const pieData = data ? [
    { name: "Bulundu",    value: data.found_count,            fill: "#059669" },
    { name: "Bulunamadı", value: data.not_found_count,        fill: "#EF4444" },
    { name: "Çoklu",      value: data.multiple_matches_count, fill: "#F59E0B" },
  ].filter(d => d.value > 0) : [];

  const foundRate = data ? pct(data.found_count, data.total_requests) : "—";

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Uçar Price API istatistikleri</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Period filter */}
            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  data-testid={`period-${p.key}`}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                    period === p.key
                      ? "bg-[#111827] text-white shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Refresh */}
            <button
              data-testid="refresh-btn"
              onClick={() => fetchDashboard(true)}
              disabled={refreshing}
              className="p-2 border border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors shadow-sm"
              title="Yenile"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* Active List Banner */}
            {data.active_list ? (
              <div className="mb-6 flex items-center gap-3 p-3.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4.5 h-4.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-green-800">Aktif Liste: </span>
                  <span className="text-sm text-green-700">{data.active_list.name}</span>
                  <span className="text-xs text-green-500 ml-2">· {data.active_model_count} model</span>
                </div>
                {data.active_list.activated_at && (
                  <span className="text-xs text-green-400 shrink-0 hidden md:block">
                    {formatDt(data.active_list.activated_at)}
                  </span>
                )}
              </div>
            ) : (
              <div className="mb-6 flex items-center gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                <p className="text-sm text-amber-700 font-medium">Aktif fiyat listesi yok — bir listeyi aktifleştirin.</p>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="Toplam İstek"
                value={data.total_requests.toLocaleString("tr-TR")}
                sub={PERIODS.find(p => p.key === period)?.label}
                icon={Activity}
                color="text-gray-900"
                accent="bg-gray-100 text-gray-500"
              />
              <StatCard
                label="Bulundu"
                value={data.found_count.toLocaleString("tr-TR")}
                sub={`${foundRate} başarı oranı`}
                icon={CheckCircle2}
                color="text-green-600"
                accent="bg-green-100 text-green-600"
              />
              <StatCard
                label="Bulunamadı"
                value={data.not_found_count.toLocaleString("tr-TR")}
                sub={pct(data.not_found_count, data.total_requests) + " hata oranı"}
                icon={XCircle}
                color="text-red-500"
                accent="bg-red-100 text-red-500"
              />
              <StatCard
                label="Çoklu Eşleşme"
                value={data.multiple_matches_count.toLocaleString("tr-TR")}
                sub="Belirsiz model adı"
                icon={Layers}
                color="text-amber-500"
                accent="bg-amber-100 text-amber-500"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Bar Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-bold uppercase tracking-wide text-gray-800"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    API İstekleri
                  </h2>
                  <span className="text-xs text-gray-400">
                    {period === "today" ? "Saatlik" : "Günlük"}
                  </span>
                </div>
                {data.chart_data?.some(d => (d.found + d.not_found + d.multiple) > 0) ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.chart_data} barSize={period === "today" ? 10 : 14}
                      margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9CA3AF" }}
                        tickLine={false} axisLine={false}
                        interval={period === "today" ? 3 : period === "month" ? 4 : 0} />
                      <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="found" name="Bulundu" stackId="a" fill="#059669" radius={[0,0,0,0]} />
                      <Bar dataKey="not_found" name="Bulunamadı" stackId="a" fill="#EF4444" radius={[0,0,0,0]} />
                      <Bar dataKey="multiple" name="Çoklu" stackId="a" fill="#F59E0B" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                    Bu dönemde veri yok
                  </div>
                )}
                {/* Legend */}
                <div className="flex items-center gap-4 mt-3 justify-center">
                  {[["#059669","Bulundu"],["#EF4444","Bulunamadı"],["#F59E0B","Çoklu Eşleşme"]].map(([c,l]) => (
                    <div key={l} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: c }} />
                      <span className="text-xs text-gray-500">{l}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right column: pie + top models */}
              <div className="flex flex-col gap-4">
                {/* Donut / ratio */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h2 className="text-base font-bold uppercase tracking-wide text-gray-800 mb-3"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    Başarı Oranı
                  </h2>
                  {data.total_requests > 0 ? (
                    <div className="flex items-center gap-4">
                      <div className="relative w-20 h-20 shrink-0">
                        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F3F4F6" strokeWidth="3.5" />
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#059669" strokeWidth="3.5"
                            strokeDasharray={`${(data.found_count / data.total_requests) * 100} 100`}
                            strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold text-green-600" style={{ fontFamily: "Barlow Condensed" }}>
                            {foundRate}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1.5 text-xs flex-1">
                        {[
                          { label: "Bulundu",     val: data.found_count,            color: "bg-green-500" },
                          { label: "Bulunamadı",  val: data.not_found_count,        color: "bg-red-500" },
                          { label: "Çoklu",       val: data.multiple_matches_count, color: "bg-amber-400" },
                        ].map(({ label, val, color }) => (
                          <div key={label} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${color} shrink-0`} />
                              <span className="text-gray-600">{label}</span>
                            </div>
                            <span className="font-bold text-gray-800">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">Veri yok</p>
                  )}
                </div>

                {/* Top models */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex-1">
                  <h2 className="text-base font-bold uppercase tracking-wide text-gray-800 mb-3"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    En Çok Sorgulanan
                  </h2>
                  {data.top_models?.length > 0 ? (
                    <div className="space-y-2">
                      {data.top_models.slice(0, 5).map((m, i) => {
                        const maxCount = data.top_models[0]?.count || 1;
                        return (
                          <div key={m.model} className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-300 w-4 shrink-0">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-medium text-gray-700 truncate">{m.model}</span>
                                <span className="text-xs font-bold text-gray-500 shrink-0 ml-1">{m.count}</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#DC2626] rounded-full transition-all"
                                  style={{ width: `${(m.count / maxCount) * 100}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 text-center py-4">Bu dönemde veri yok</p>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Logs */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold uppercase tracking-wide text-gray-800"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    Son API İstekleri
                  </h2>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Son 20</span>
                </div>
                <a href="/loglar" className="text-xs text-[#DC2626] hover:underline font-medium">
                  Tüm loglar →
                </a>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recent-logs-table">
                  <thead>
                    <tr className="bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-3 text-left">Tarih / Saat</th>
                      <th className="px-4 py-3 text-left">Model Metni</th>
                      <th className="px-4 py-3 text-left">Marka</th>
                      <th className="px-4 py-3 text-left">Sonuç</th>
                      <th className="px-4 py-3 text-left">Bulunan Model</th>
                      <th className="px-4 py-3 text-left">API Anahtarı</th>
                      <th className="px-4 py-3 text-right">ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_logs.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                          Bu dönemde henüz istek yok
                        </td>
                      </tr>
                    ) : (
                      data.recent_logs.map((log) => (
                        <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-gray-500 text-xs font-mono whitespace-nowrap">
                            {formatDt(log.created_at)}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800 max-w-[180px] truncate">
                            {log.model_text}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{log.brand || "—"}</td>
                          <td className="px-4 py-3">
                            {log.multiple_matches ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                <Layers className="w-3 h-3" /> Çoklu
                              </span>
                            ) : log.found ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" /> Bulundu
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                <XCircle className="w-3 h-3" /> Bulunamadı
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs font-medium">{log.result_model || "—"}</td>
                          <td className="px-4 py-3">
                            {log.api_key_name && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                                {log.api_key_name}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-gray-400">
                            {log.response_time_ms}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
